/**
 * Desktop notifications for messages that arrive while a tab is OPEN.
 *
 * FCM splits push delivery in two, and only one half is automatic:
 *
 *  - tab closed or backgrounded → the service worker's `onBackgroundMessage`
 *    fires and `src/firebase-messaging-sw.js` calls `showNotification` itself;
 *  - tab open and focused → the worker is bypassed, `onMessage` fires in the
 *    page, and **nothing is displayed unless the page displays it**.
 *
 * That second case is the common one — a manager watching the cockpit is the
 * person a new card concerns most — and without this service it produced
 * silence. Which reads exactly like a broken feature.
 *
 * Three decisions worth keeping:
 *
 * **Display through the service worker registration, not `new Notification()`.**
 * A notification created by the page has no `notificationclick` handler behind
 * it, so clicking it does nothing. Going through `registration.showNotification`
 * routes the click into the worker's existing handler and the deep link works
 * the same whether the tab was open or shut.
 *
 * **Coalesce by tag.** Every tab receives the same push, and a burst of cards
 * lands in the same workspace. `tag` makes the browser REPLACE rather than
 * stack, and the debounce below turns a burst into one "N nouveaux éléments"
 * instead of N notifications racing to overwrite each other.
 *
 * **Re-check the active window here too.** The backend already skipped
 * out-of-window pushes using the stored `profile.timezone`, and the worker
 * re-checks against the real clock. A foreground message reaches neither of
 * those paths, so the same check has to run once more — otherwise the one
 * delivery route the user watches most is the one that ignores their quiet
 * hours.
 */

import { Injectable, inject } from '@angular/core';

import { FcmService } from '@core/services/infra/fcm.service';
import { LoggerService } from '@core/services/infra/logger.service';
import { isWithinActiveHours } from '@shared/definitions/notification-quiet-hours';

/** The FCM data payload we care about; everything is a string over the wire. */
export interface ForegroundPushData {
  readonly tag?: string;
  readonly link?: string;
  readonly priority?: string;
  readonly activeHoursStart?: string;
  readonly activeHoursEnd?: string;
}

/** The shape `onMessage` hands back. Narrowed from `unknown` at the boundary. */
export interface ForegroundPushPayload {
  readonly notification?: { title?: string; body?: string };
  readonly data?: ForegroundPushData;
}

/** What a coalesced group renders as, once the debounce window closes. */
export interface CoalescedNotification {
  readonly tag: string;
  readonly title: string;
  readonly body: string;
  readonly link: string;
  readonly count: number;
}

/**
 * How long to hold a message before showing it, so siblings can join.
 *
 * 1.2s is under the threshold where a user would call the notification "late",
 * and comfortably over the spread of a fan-out that writes several documents in
 * one backend pass.
 */
export const COALESCE_WINDOW_MS = 1200;

/**
 * Should a foreground payload be displayed at all?
 *
 * Pure, so the quiet-hours contract is testable without a browser. Mirrors
 * `shouldDisplay` in `src/firebase-messaging-sw.js`: `urgent` always passes —
 * that exemption is what makes an active window acceptable in the first place —
 * and a payload carrying no window is shown rather than dropped, because losing
 * a notification is worse than one arriving at an awkward hour.
 *
 * @param data The FCM data payload.
 * @param hour The current local hour, 0-23.
 * @return True when the notification may interrupt.
 */
export function shouldDisplayForeground(data: ForegroundPushData, hour: number): boolean {
  if (data.priority === 'urgent') return true;
  const start = Number(data.activeHoursStart);
  const end = Number(data.activeHoursEnd);
  if (!Number.isInteger(start) || !Number.isInteger(end)) return true;
  return isWithinActiveHours(hour, { start, end });
}

@Injectable({ providedIn: 'root' })
export class DesktopNotificationsService {
  private readonly fcm = inject(FcmService);
  private readonly logger = inject(LoggerService);

  private unsubscribe: (() => void) | null = null;
  /** One pending group per tag; the timer fires once the window closes. */
  private readonly pending = new Map<string, { group: CoalescedNotification; timer: number }>();

  /**
   * Start listening for foreground messages.
   *
   * Idempotent: several shells may call it (a user can switch between the
   * manager layout and the tenant shell without a reload) and a second
   * subscription would double every notification.
   */
  async start(): Promise<void> {
    if (this.unsubscribe) return;
    if (!this.fcm.isConfigured()) return;
    if (this.fcm.permissionState() !== 'granted') return;

    this.unsubscribe = await this.fcm.onForegroundMessage((payload) => {
      this.handle(payload as ForegroundPushPayload);
    });
  }

  /** Stop listening and drop anything still waiting out its window. */
  stop(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    for (const { timer } of this.pending.values()) clearTimeout(timer);
    this.pending.clear();
  }

  private handle(payload: ForegroundPushPayload): void {
    const data = payload.data ?? {};
    if (!shouldDisplayForeground(data, new Date().getHours())) return;

    // An untagged payload gets a unique key so it is never merged with an
    // unrelated one. Grouping is opt-in, decided by the backend.
    const tag = data.tag || `untagged:${this.pending.size}:${performance.now()}`;
    const existing = this.pending.get(tag);

    if (existing) {
      clearTimeout(existing.timer);
      // Deliberately keeping the FIRST title/body rather than the latest: the
      // count already says more arrived, and a body that mutates while the
      // window is open makes the notification unreproducible in a bug report.
      const group: CoalescedNotification = { ...existing.group, count: existing.group.count + 1 };
      this.pending.set(tag, { group, timer: this.schedule(tag) });
      return;
    }

    const group: CoalescedNotification = {
      tag,
      title: payload.notification?.title || 'Proporio',
      body: payload.notification?.body || '',
      link: data.link || '/',
      count: 1,
    };
    this.pending.set(tag, { group, timer: this.schedule(tag) });
  }

  private schedule(tag: string): number {
    return setTimeout(() => {
      const entry = this.pending.get(tag);
      this.pending.delete(tag);
      if (entry) void this.show(entry.group);
    }, COALESCE_WINDOW_MS) as unknown as number;
  }

  /**
   * Hand the group to the service worker registration.
   *
   * Failure is logged and swallowed: a notification that cannot be shown must
   * never take down the page the user is actually working in.
   */
  private async show(group: CoalescedNotification): Promise<void> {
    try {
      if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
      const registration = await navigator.serviceWorker.getRegistration(
        '/firebase-cloud-messaging-push-scope',
      );
      if (!registration) return;

      await registration.showNotification(group.title, {
        body: group.count > 1 ? `${group.body} (+${group.count - 1})` : group.body,
        icon: '/assets/img/logo/icon-192.png',
        badge: '/assets/img/logo/badge-72.png',
        tag: group.tag,
        data: { link: group.link },
      });
    } catch (err) {
      this.logger.warn(
        '[DesktopNotifications] could not show notification',
        'DesktopNotificationsService',
        err,
      );
    }
  }
}
